import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  HStack,
  Image,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import {
  useFinancialClientsByCategory,
  useFinancialClientsByCategoryMonths,
  useFinancialClientsByYears,
  useFinancialPlansByCategory,
} from '@partiu/api/dashboard/hooks.ts';
import { categoryBadge } from './badges.ts';
import {
  agreementYears,
  fieldProps,
  FinancialCard,
  FinancialNotice,
  FinancialPagination,
  FinancialShell,
  FinancialTabs,
  financialErrorMessage,
  tableSx,
} from './shared.tsx';

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function formatUpdatedAt(value?: string | null): string | null {
  if (!value) return null;
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, "dd/MM/yyyy 'às' HH:mm");
}

function CategoryMark({ name, rank, size = '48px' }: { name: string; rank?: number; size?: string }) {
  const src = categoryBadge(name, rank);
  if (!src) {
    return (
      <Flex boxSize={size} borderRadius="full" bg="gray.100" align="center" justify="center" flexShrink={0}>
        <Text fontWeight="700" color="gray.500" fontSize={size === '48px' ? 'md' : 'sm'}>
          {name.trim().slice(0, 1).toUpperCase() || '•'}
        </Text>
      </Flex>
    );
  }

  return <Image src={src} alt="" boxSize={size} objectFit="contain" flexShrink={0} />;
}

const MONTH_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function countOf(rows: { levelId: number; clientsCount: number }[] | undefined, levelId: number) {
  return rows?.find((row) => row.levelId === levelId)?.clientsCount ?? 0;
}

function YearReport({ year }: { year: number }) {
  const { data, isLoading, isError, error } = useFinancialClientsByCategory({ page: 1, year });
  const monthQueries = useFinancialClientsByCategoryMonths(year);
  const rows = data?.results ?? [];
  const total = rows.reduce((sum, row) => sum + (row.clientsCount || 0), 0);
  const monthError = monthQueries.find((query) => query.isError)?.error;
  const categories = new Map<number, { levelId: number; levelName: string; levelRank: number }>();
  rows.forEach((row) => categories.set(row.levelId, row));
  monthQueries.forEach((query) => {
    query.data?.results.forEach((row) => {
      if (!categories.has(row.levelId)) categories.set(row.levelId, row);
    });
  });
  const categoryRows = [...categories.values()].sort(
    (a, b) => a.levelRank - b.levelRank || a.levelName.localeCompare(b.levelName, 'pt-BR')
  );

  return (
    <VStack align="stretch" spacing={4}>
      <FinancialCard p={5}>
        <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="0.08em">
          RESUMO ANUAL
        </Text>
        {isLoading ? (
          <Spinner mt={3} size="sm" />
        ) : isError ? (
          <Text mt={2} color="red.600" fontSize="sm">
            {financialErrorMessage(error)}
          </Text>
        ) : (
          <>
            <HStack mt={2} justify="space-between" align="flex-end">
              <Heading size="lg" letterSpacing="-0.03em">
                {year}
              </Heading>
              <Box textAlign="right" bg="gray.50" borderRadius="xl" px={4} py={2}>
                <Text fontSize="2xl" fontWeight="700" lineHeight="1" letterSpacing="-0.03em">
                  {total.toLocaleString('pt-BR')}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  soma das categorias
                </Text>
              </Box>
            </HStack>
            {rows.length === 0 ? (
              <Text mt={3} fontSize="sm" color="gray.500">
                Nenhuma categoria neste ano.
              </Text>
            ) : (
              <HStack mt={4} spacing={3} align="stretch" flexWrap="wrap">
                {rows.map((row) => (
                <HStack
                  key={row.levelId}
                  spacing={3}
                  px={3}
                  py={2}
                  borderWidth="1px"
                  borderColor="gray.100"
                  borderRadius="xl"
                  bg="gray.50"
                  minW="140px"
                >
                    <CategoryMark name={row.levelName} rank={row.levelRank} size="28px" />
                    <Box>
                      <Text fontSize="sm" fontWeight="600" lineHeight="1.2">
                        {row.levelName}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {row.clientsCount.toLocaleString('pt-BR')}
                      </Text>
                    </Box>
                  </HStack>
                ))}
              </HStack>
            )}
          </>
        )}
      </FinancialCard>

      <FinancialCard>
        <Box p={5} pb={0}>
          <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="0.08em">
            MÊS A MÊS
          </Text>
        </Box>
        <Box overflowX="auto" p={5} pt={3}>
        {monthError != null ? (
          <Text mt={2} color="red.600" fontSize="sm">
            {financialErrorMessage(monthError)}
          </Text>
        ) : null}
        {categoryRows.length === 0 && monthQueries.some((query) => query.isLoading) ? (
          <Spinner mt={3} size="sm" />
        ) : categoryRows.length === 0 ? (
          <Text mt={3} fontSize="sm" color="gray.500">
            Nenhuma categoria neste ano.
          </Text>
        ) : (
          <Table size="sm" sx={{ ...tableSx, 'th, td': { whiteSpace: 'nowrap' } }}>
            <Thead>
              <Tr>
                <Th position="sticky" left={0} bg="gray.50" zIndex={1}>
                  Categoria
                </Th>
                {MONTH_SHORT.map((label) => (
                  <Th key={label} isNumeric>
                    {label}
                  </Th>
                ))}
                <Th isNumeric bg="gray.100">
                  Ano
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {categoryRows.map((category) => (
                <Tr key={category.levelId}>
                  <Td position="sticky" left={0} bg="white" zIndex={1}>
                    <HStack spacing={2}>
                      <CategoryMark name={category.levelName} rank={category.levelRank} size="24px" />
                      <Text fontSize="sm" fontWeight="600">
                        {category.levelName}
                      </Text>
                    </HStack>
                  </Td>
                  {monthQueries.map((query, index) => (
                    <Td key={MONTH_SHORT[index]} isNumeric color="gray.700">
                      {query.isLoading
                        ? '…'
                        : countOf(query.data?.results, category.levelId).toLocaleString('pt-BR')}
                    </Td>
                  ))}
                  <Td isNumeric fontWeight="700" bg="gray.50">
                    {countOf(rows, category.levelId).toLocaleString('pt-BR')}
                  </Td>
                </Tr>
              ))}
              <Tr>
                <Td position="sticky" left={0} bg="gray.50" zIndex={1} fontWeight="700" fontSize="sm">
                  Soma
                </Td>
                {monthQueries.map((query, index) => {
                  const monthTotal = (query.data?.results ?? []).reduce(
                    (sum, row) => sum + (row.clientsCount || 0),
                    0
                  );
                  return (
                    <Td key={MONTH_SHORT[index]} isNumeric fontWeight="700" bg="gray.50">
                      {query.isLoading ? '…' : monthTotal.toLocaleString('pt-BR')}
                    </Td>
                  );
                })}
                <Td isNumeric fontWeight="700" bg="gray.100">
                  {total.toLocaleString('pt-BR')}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        )}
        </Box>
      </FinancialCard>
    </VStack>
  );
}

function AllYears({ years }: { years: number[] }) {
  const queries = useFinancialClientsByYears(years);
  const pending = queries.some((query) => query.isLoading);
  const failed = queries.find((query) => query.isError);
  const visible = years.filter((_, index) => (queries[index]?.data?.results.length ?? 0) > 0);

  if (pending && visible.length === 0) return <Spinner />;
  if (failed && visible.length === 0) {
    return <Text color="red.600">{financialErrorMessage(failed.error)}</Text>;
  }
  if (!pending && visible.length === 0) {
    return <Text color="gray.500">Nenhuma categoria nestes anos.</Text>;
  }

  return (
    <VStack align="stretch" spacing={8}>
      {visible.map((year) => (
        <YearReport key={year} year={year} />
      ))}
    </VStack>
  );
}

function AnnualTab() {
  const { extraData } = useTheme();
  const currentYear = new Date().getFullYear();
  const years = agreementYears(extraData.startDate, extraData.createdAt);
  const [year, setYear] = useState<number | 'all'>(currentYear);

  return (
    <VStack align="stretch" spacing={4}>
      <Box maxW="200px">
        <Text fontSize="xs" mb={1.5} color="gray.500" fontWeight="600">
          Ano
        </Text>
        <Select
          value={year === 'all' ? 'all' : String(year)}
          onChange={(event) => {
            const next = event.target.value;
            setYear(next === 'all' ? 'all' : Number(next));
          }}
          {...fieldProps}
          bg="white"
        >
          <option value="all">Todos os anos</option>
          {years.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
      </Box>
      {year === 'all' ? <AllYears years={years} /> : <YearReport year={year} />}
    </VStack>
  );
}

function ClientsTab() {
  const { extraData } = useTheme();
  const currentYear = new Date().getFullYear();
  const years = agreementYears(extraData.startDate, extraData.createdAt);
  const [year, setYear] = useState<number | ''>(currentYear);
  const [month, setMonth] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, isFetching } = useFinancialClientsByCategory({
    page,
    year: year === '' ? undefined : year,
    month: year !== '' && month !== '' ? month : undefined,
  });
  const updatedAt = formatUpdatedAt(data?.rebuiltAt);
  const results = data?.results ?? [];

  return (
    <VStack align="stretch" spacing={4}>
      <FinancialCard p={4}>
      <HStack spacing={3} align="flex-end" flexWrap="wrap">
        <Box minW="140px">
          <Text fontSize="xs" mb={1.5} color="gray.500" fontWeight="600">
            Ano
          </Text>
          <Select
            value={year === '' ? '' : String(year)}
            onChange={(event) => {
              const next = event.target.value;
              setYear(next === '' ? '' : Number(next));
              if (next === '') setMonth('');
              setPage(1);
            }}
            {...fieldProps}
            bg="white"
            maxW="180px"
          >
            <option value="">Todo o período</option>
            {years.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </Box>
        <Box minW="160px">
          <Text fontSize="xs" mb={1.5} color="gray.500" fontWeight="600">
            Mês
          </Text>
          <Select
            value={month === '' ? '' : String(month)}
            isDisabled={year === ''}
            onChange={(event) => {
              const next = event.target.value;
              setMonth(next === '' ? '' : Number(next));
              setPage(1);
            }}
            {...fieldProps}
            bg="white"
            maxW="200px"
          >
            <option value="">Todos</option>
            {MONTHS.map((label, index) => (
              <option key={label} value={index + 1}>
                {label}
              </option>
            ))}
          </Select>
        </Box>
        {updatedAt && (
          <Text fontSize="sm" color="gray.500" pb={2}>
            Atualizado em {updatedAt}
          </Text>
        )}
      </HStack>
      </FinancialCard>

      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <FinancialNotice tone="error">{financialErrorMessage(error)}</FinancialNotice>
      ) : results.length === 0 ? (
        <FinancialNotice>Nenhuma categoria encontrada.</FinancialNotice>
      ) : (
        <VStack align="stretch" spacing={3}>
          {results.map((row) => (
            <HStack
              key={row.levelId}
              bg="white"
              borderWidth="1px"
              borderColor="gray.100"
              boxShadow="sm"
              borderRadius="2xl"
              px={5}
              py={4}
              justify="space-between"
              spacing={4}
            >
              <HStack spacing={4} minW={0}>
                <CategoryMark name={row.levelName} rank={row.levelRank} />
                <Text fontWeight="700" fontSize="md" noOfLines={2}>
                  {row.levelName}
                </Text>
              </HStack>
              <Box textAlign="right" flexShrink={0} bg="gray.50" borderRadius="xl" px={4} py={2} minW="108px">
                <Text fontSize="2xl" fontWeight="700" lineHeight="1" letterSpacing="-0.03em">
                  {row.clientsCount.toLocaleString('pt-BR')}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {row.clientsCount === 1 ? 'cliente' : 'clientes'}
                </Text>
              </Box>
            </HStack>
          ))}
        </VStack>
      )}

      {!isError && (
        <FinancialPagination
          page={page}
          count={data?.count}
          hasPrevious={!!data?.previous}
          hasNext={!!data?.next}
          isLoading={isFetching}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => current + 1)}
          itemLabel={['categoria', 'categorias']}
        />
      )}
    </VStack>
  );
}

function PlansTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, isFetching } = useFinancialPlansByCategory(page);
  const results = data?.results ?? [];

  return (
    <VStack align="stretch" spacing={4}>
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <FinancialNotice tone="error">{financialErrorMessage(error)}</FinancialNotice>
      ) : results.length === 0 ? (
        <FinancialNotice>Nenhuma categoria encontrada.</FinancialNotice>
      ) : (
        <Accordion allowToggle display="flex" flexDirection="column" gap={3}>
            {results.map((row, index) => (
              <AccordionItem
                key={`${row.levelId}-${index}`}
                border="1px solid"
                borderColor="gray.100"
                borderRadius="2xl"
                bg="white"
                boxShadow="sm"
                overflow="hidden"
              >
                <AccordionButton px={5} py={4} _hover={{ bg: 'gray.50' }} _expanded={{ bg: 'gray.50' }}>
                  <HStack flex="1" spacing={4} minW={0}>
                    <CategoryMark name={row.levelName} rank={row.levelRank} />
                    <Box textAlign="left" minW={0}>
                      <Text fontWeight="700" noOfLines={1}>
                        {row.levelName}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {row.plansCount.toLocaleString('pt-BR')} {row.plansCount === 1 ? 'plano' : 'planos'}
                      </Text>
                    </Box>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={4} py={4} bg="gray.50">
                  {row.plans.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      Nenhum plano nesta categoria.
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={2} maxH="420px" overflowY="auto" pr={1}>
                      {row.plans.map((plan) => {
                        const sameCategory =
                          plan.planName.trim().toLowerCase() === row.levelName.trim().toLowerCase();
                        const planLabel = sameCategory ? '' : plan.planName;
                        const productLabel = plan.productId != null ? plan.productName?.trim() : '';
                        const subscriptions = plan.subscriptionsCount ?? 0;

                        return (
                          <HStack key={plan.planId} bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="xl" px={4} py={3} spacing={4} align="center">
                            <Box flex="1" minW={0}>
                              <Text fontWeight="600">
                                {planLabel || productLabel ? (
                                  <>
                                    {planLabel}
                                    {planLabel && productLabel ? ' · ' : ''}
                                    {productLabel}
                                  </>
                                ) : (
                                  plan.planSlugIntegration || '—'
                                )}
                              </Text>
                              {(planLabel || productLabel) && plan.planSlugIntegration && (
                                <Text fontSize="sm" color="gray.500">
                                  {plan.planSlugIntegration}
                                </Text>
                              )}
                            </Box>
                            <Box textAlign="right" flexShrink={0}>
                              <Text fontWeight="700">{subscriptions.toLocaleString('pt-BR')}</Text>
                              <Text fontSize="xs" color="gray.500">
                                {subscriptions === 1 ? 'assinatura' : 'assinaturas'}
                              </Text>
                            </Box>
                          </HStack>
                        );
                      })}
                    </VStack>
                  )}
                </AccordionPanel>
              </AccordionItem>
            ))}
        </Accordion>
      )}

      {!isError && (
        <FinancialPagination
          page={page}
          count={data?.count}
          hasPrevious={!!data?.previous}
          hasNext={!!data?.next}
          isLoading={isFetching}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => current + 1)}
          itemLabel={['categoria', 'categorias']}
        />
      )}
    </VStack>
  );
}

export function FinancialClientsPage() {
  const { colors } = useTheme();
  const [tab, setTab] = useState<'clients' | 'annual'>('clients');
  const primary = colors.primaryColor || '#1a202c';

  return (
    <FinancialShell title="Clientes por categoria" description="Lista do período e o resumo anual.">
      <FinancialTabs
        value={tab}
        onChange={setTab}
        color={primary}
        options={[
          { id: 'clients', label: 'Clientes por categoria' },
          { id: 'annual', label: 'Resumo anual' },
        ]}
      />
      {tab === 'clients' && <ClientsTab />}
      {tab === 'annual' && <AnnualTab />}
    </FinancialShell>
  );
}

export function FinancialPlansPage() {
  return (
    <FinancialShell title="Planos por categoria" description="Planos agrupados por categoria." maxW="880px">
      <PlansTab />
    </FinancialShell>
  );
}
